import React, { Fragment, useEffect, useMemo, useState } from 'react';
import * as PropTypes from 'prop-types';
import get from 'lodash/get';
import { useDispatch } from 'react-redux';

import visitTypePlaceholder from '../../../assets/icons/png/visitTypePlaceholder.png';
import { redirectToUsers } from '../../../utils/appointments/redirect-utils';
import Shape from '../../common/Shape';
import AppointmentHeader from '../Components/AppointmentHeader';
import EmptyContent from '../../common/EmptyState/Content';
import Popup from '../../common/Popup';
import Button from '../../Button';
import Icon from '../../Icon';
import AppointmentMyProviders from '../AppointmentMyProviders';
import {
  APPOINTMENT_ALLOWED_TYPES,
  URGENT_CARE_TYPE_CODE,
} from '../../../constants';
import { usePageView } from '../../../hocs/usePageView';
import MetaHelmet from '../../AppHelmet/MetaHelmet';
import { enhance } from './enhance';
import { appointmentProgressActions } from '../../../redux/appointmentForm/progress-types';
import styles from './styles.styl';

function AppointmentVisitType({
  init,
  types,
  selectVisitType,
  programBasedVisits,
  programBasedVisitId,
  selectProgramBasedVisit,
  intl: { formatMessage },
}) {
  const [open, setOpen] = useState(false);
  const [externalLink, setExternalLink] = useState('');
  const [selectedType, setSelectedType] = useState('');

  const activeTypes = useMemo(
    () => types.filter(({ code }) => APPOINTMENT_ALLOWED_TYPES.includes(code)),
    [types],
  );

  usePageView('Get Care | Visit Type');

  useEffect(() => {
    init();
  }, []);

  function renderImage(type) {
    const src =
      get(type, 'icon.url') ||
      get(type, 'organization.logo.thumbMin') ||
      visitTypePlaceholder;
    return () => <img alt="icon" style={{ width: 40 }} src={src} />;
  }

  const handleBack = () => {
    if (programBasedVisitId) {
      init();
    } else redirectToUsers();
  };

  const emptyState = useMemo(
    () => [activeTypes.length, programBasedVisits.length].every(el => !el),
    [activeTypes, programBasedVisits],
  );

  const handleSelectVisitType = type => {
    if (type.code === URGENT_CARE_TYPE_CODE) {
      setOpen(true);
      if (type.additionalInfoLink) {
        setExternalLink(type.additionalInfoLink);
      }
      setSelectedType(type);
    } else {
      selectVisitType(type);
    }
  };

  const handleMoreInfo = () => {
    if (externalLink) {
      window.open(externalLink, '_blank');
      setOpen(false);
    } else {
      setOpen(false);
    }
  };

  const handleProceed = () => {
    setOpen(false);
    selectVisitType(selectedType);
  };

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(appointmentProgressActions.setReason(null));
    dispatch(appointmentProgressActions.setAdditionalInfoFormData(null));
    dispatch(appointmentProgressActions.setPrimaryCareProviderFormData(null));
  }, []);

  return (
    <Fragment>
      <MetaHelmet title="pages.title.getCare.visitType" />
      <AppointmentHeader handleBack={handleBack} />
      <AppointmentMyProviders />
      <h2 className={styles.title}>
        {formatMessage({ id: 'appointments.visitType' })}
      </h2>
      <div>
        {programBasedVisits.map(visit => (
          <Shape
            renderPrimary={() => visit.name}
            onClick={() => selectProgramBasedVisit(visit)}
            renderSecondary={() => visit.description}
            renderLeft={renderImage(visit)}
            key={visit.id}
          />
        ))}
        {activeTypes.map(type => (
          <Shape
            renderPrimary={() => type.name}
            onClick={() => handleSelectVisitType(type)}
            renderSecondary={() => type.description}
            renderLeft={renderImage(type)}
            key={type.id}
          />
        ))}
        {emptyState && (
          <EmptyContent
            className={styles.emptyBlock}
            text={formatMessage({ id: `appointments.emptyVisitTypes` })}
            icon="search-list"
            size={132}
          />
        )}
        {open && (
          <Popup
            opened={open}
            onClose={() => setOpen(false)}
            withHeading={false}
            mobileFullScreen={false}
            width="notification"
            contentNoPadding
          >
            <div className={styles.modalWrapper}>
              <Icon name="information" size={48} />
              <h1 className={styles.modalTitle}>
                {formatMessage({ id: 'common.important' })}
              </h1>

              <div className={styles.description}>
                {formatMessage({
                  id: 'visitType.urgentCare.description',
                })}
              </div>

              <div className={styles.buttonsWrapper}>
                <Button color="accent" onClick={handleMoreInfo}>
                  {formatMessage({ id: 'common.moreInfo' })}
                </Button>
                <Button color="primary" onClick={handleProceed}>
                  {formatMessage({ id: 'submit.expiredToken' })}
                </Button>
              </div>
            </div>
          </Popup>
        )}
      </div>
    </Fragment>
  );
}

AppointmentVisitType.propTypes = {
  init: PropTypes.func,
  types: PropTypes.array,
  intl: PropTypes.object,
  selectVisitType: PropTypes.func,
  programBasedVisits: PropTypes.array,
  programBasedVisitId: PropTypes.number,
  selectProgramBasedVisit: PropTypes.func,
};

export default enhance(AppointmentVisitType);



// WEBPACK FOOTER //
// ./src/components/AppointmentForm/AppointmentVisitType/index.jsx